import type { ReactNode } from "react";
import { ArrowLeft, ArrowRight, Loader2, Calendar, Shield, User, ListChecks } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import type { Season } from "../types";

function Chip({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-3 py-1.5">
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
      {children}
    </span>
  );
}

type SummaryFooterProps = {
  selectedSeason: Season[];
  selectedTeam: (string | number)[];
  selectedPlayer: (string | number)[];
  selectAllTeamPlayers: (string | number)[];
  selectedEvent: string[];
  isSelectAllEvent: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
};

export function SummaryFooter({
  selectedSeason,
  selectedTeam,
  selectedPlayer,
  selectAllTeamPlayers,
  selectedEvent,
  isSelectAllEvent,
  isSubmitting,
  onBack,
  onSubmit,
}: SummaryFooterProps) {
  const disabled =
    selectedTeam.length === 0 ||
    (selectedPlayer.length === 0 && selectAllTeamPlayers.length === 0) ||
    (selectedEvent.length === 0 && !isSelectAllEvent) ||
    isSubmitting;

  const seasonLabel = selectedSeason.length > 0 ? `${selectedSeason.length} season(s)` : "No season";
  const teamsLabel = selectedTeam.length > 0 ? `${selectedTeam.length} team(s)` : "No teams";
  const playersLabel =
    selectedPlayer.length > 0 || selectAllTeamPlayers.length > 0
      ? `${selectedPlayer.length} player(s)${selectAllTeamPlayers.length > 0 ? ` + ${selectAllTeamPlayers.length} team(s) all` : ""}`
      : "No players";
  const actionsLabel =
    isSelectAllEvent && selectedEvent.length === 0
      ? "All actions"
      : selectedEvent.length > 0
        ? `${selectedEvent.length} action(s)`
        : "Choose actions to finish";

  return (
    <div className="sticky bottom-0 rounded-xl border border-slate-200 bg-card p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold text-slate-400 shrink-0" style={{ letterSpacing: "0.04em" }}>
          YOUR SPOTLIGHT
        </span>
        <Chip icon={Calendar}>{seasonLabel}</Chip>
        <Chip icon={Shield}>{teamsLabel}</Chip>
        <Chip icon={User}>{playersLabel}</Chip>
        <Chip icon={ListChecks}>{actionsLabel}</Chip>
      </div>
      <div className="flex gap-2 w-full sm:w-auto">
        <Button variant="outline" onClick={onBack} className="flex-1 sm:flex-none text-base">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <Button onClick={onSubmit} disabled={disabled} className="flex-1 sm:flex-none text-base">
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Build Your Spotlight
        </Button>
      </div>
    </div>
  );
}
